/**
 * key_value renderer.
 *
 * Renderer for arbitrary key-value data.
 *
 * - Keys prefixed with `_` are filtered out (internal metadata).
 * - Keys formatted as `snake_case -> Title Case`.
 * - Scalar values render as strings; arrays render as bullet lists; nested
 *   objects render as a nested key-value block (single level only).
 * - Empty strings render as `---`.
 */

import type { CSSProperties } from 'react'
import {
  snakeToTitleCase,
  displayScalar,
  EMPTY_PLACEHOLDER,
} from './shared'

export interface KeyValueRendererProps {
  data: unknown
}

export function KeyValueRenderer({ data }: KeyValueRendererProps) {
  const normalized = normalizeInput(data)

  if (normalized === null || Object.keys(normalized).length === 0) {
    return (
      <p style={styles.noDataText}>
        No data provided.
      </p>
    )
  }

  return <KeyValueBlock entries={normalized} depth={0} />
}

/**
 * Normalize arbitrary input into an object for rendering.
 */
function normalizeInput(data: unknown): Record<string, unknown> | null {
  if (data === null || data === undefined) {
    return null
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    return { value: data }
  }

  return data as Record<string, unknown>
}

interface KeyValueBlockProps {
  entries: Record<string, unknown>
  depth: number
}

/**
 * Recursive key-value block renderer.
 */
function KeyValueBlock({ entries, depth }: KeyValueBlockProps) {
  const filteredKeys = Object.keys(entries).filter(
    (key) => !key.startsWith('_'),
  )

  if (filteredKeys.length === 0) {
    return (
      <p style={styles.noDataText}>
        {EMPTY_PLACEHOLDER}
      </p>
    )
  }

  return (
    <dl style={styles.definitionList}>
      {filteredKeys.map((key) => (
        <KeyValueRow
          key={key}
          label={snakeToTitleCase(key)}
          value={entries[key]}
          depth={depth}
        />
      ))}
    </dl>
  )
}

interface KeyValueRowProps {
  label: string
  value: unknown
  depth: number
}

function KeyValueRow({ label, value, depth }: KeyValueRowProps) {
  return (
    <div style={styles.row}>
      <dt style={styles.label}>
        {label}
      </dt>
      <dd style={styles.value}>
        <ValueRenderer value={value} depth={depth} />
      </dd>
    </div>
  )
}

interface ValueRendererProps {
  value: unknown
  depth: number
}

function ValueRenderer({ value, depth }: ValueRendererProps) {
  // Null / undefined
  if (value === null || value === undefined) {
    return <span style={styles.mutedText}>{EMPTY_PLACEHOLDER}</span>
  }

  // Arrays render as bullet lists
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span style={styles.mutedText}>{EMPTY_PLACEHOLDER}</span>
    }

    return (
      <ul style={styles.bulletList}>
        {value.map((item, index) => (
          <li key={index} style={styles.bulletItem}>
            {isScalar(item) ? displayScalar(item) : <InlineJson value={item} />}
          </li>
        ))}
      </ul>
    )
  }

  // Nested objects
  if (typeof value === 'object') {
    // At depth >= 1, show as JSON
    if (depth >= 1) {
      return (
        <div style={styles.jsonWrapper}>
          <pre style={styles.jsonPre}>
            {safeStringify(value)}
          </pre>
        </div>
      )
    }

    // Depth 0: render nested key-value block
    const nestedObj = value as Record<string, unknown>
    const keys = Object.keys(nestedObj).filter((k) => !k.startsWith('_'))

    if (keys.length === 0) {
      return <span style={styles.mutedText}>{EMPTY_PLACEHOLDER}</span>
    }

    return (
      <div style={styles.nestedBlock}>
        <KeyValueBlock entries={nestedObj} depth={depth + 1} />
      </div>
    )
  }

  // Scalars
  return <span>{displayScalar(value)}</span>
}

function isScalar(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  )
}

function InlineJson({ value }: { value: unknown }) {
  try {
    return (
      <code style={styles.inlineCode}>
        {JSON.stringify(value)}
      </code>
    )
  } catch {
    return <span>{String(value)}</span>
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const styles: Record<string, CSSProperties> = {
  noDataText: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  definitionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '14px',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontWeight: 500,
    color: 'var(--text-muted)',
  },
  value: {
    color: 'var(--text-primary)',
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
  },
  mutedText: {
    color: 'var(--text-muted)',
  },
  bulletList: {
    marginLeft: '16px',
    listStyleType: 'disc',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  bulletItem: {
    color: 'var(--text-primary)',
  },
  jsonWrapper: {
    marginTop: '4px',
  },
  jsonPre: {
    maxHeight: '160px',
    overflow: 'auto',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-tertiary)',
    padding: '8px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
  nestedBlock: {
    marginLeft: '16px',
    marginTop: '4px',
    borderLeft: '2px solid var(--border-default)',
    paddingLeft: '12px',
  },
  inlineCode: {
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-tertiary)',
    padding: '2px 4px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
}

export default KeyValueRenderer
